import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ordersApi } from '../services/api';
import type { Order } from '../types';

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const businessIdFilter = searchParams.get('businessId') || '';

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await ordersApi.getMyOrders(1, 50);
      const list = res?.data ?? res?.orders ?? [];
      setOrders(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Load orders failed:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return '#28a745';
      case 'shipped': return '#17a2b8';
      case 'processing': case 'confirmed': return '#ffc107';
      case 'pending': return '#6c757d';
      case 'cancelled': case 'refunded': return '#dc3545';
      default: return '#666';
    }
  };

  const formatDate = (s: string) => new Date(s).toLocaleDateString(undefined, { dateStyle: 'medium' });

  const filteredOrders = orders.filter((order) => {
    const businessId = order.businessId || order.business?.id || '';
    const businessName = (order.businessName || order.business?.name || '').toLowerCase();
    const query = search.trim().toLowerCase();
    const matchesBusiness = !businessIdFilter || businessId === businessIdFilter;
    const matchesSearch = !query || businessName.includes(query) || (order.orderNumber || '').toLowerCase().includes(query);
    return matchesBusiness && matchesSearch;
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 32 }}>⏳</div>
        <p style={{ color: '#666', marginTop: 12 }}>Loading orders...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>📦 My Orders</h1>
      <p style={{ margin: '10px 0 30px 0', color: '#666' }}>Track orders from all Mango businesses</p>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by business or order number..."
          style={{ width: '100%', maxWidth: 340, padding: '10px 12px', border: '1px solid #ddd', borderRadius: 10 }}
        />
        {businessIdFilter && (
          <button
            onClick={() => navigate('/orders')}
            style={{ padding: '10px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, color: '#9a3412', cursor: 'pointer', fontWeight: 600 }}
          >
            Clear Business Filter
          </button>
        )}
      </div>

      {filteredOrders.length === 0 ? (
        <div style={{
          background: 'white',
          padding: 60,
          borderRadius: 16,
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          <span style={{ fontSize: 64 }}>📦</span>
          <h2 style={{ margin: '20px 0 10px 0', color: '#333' }}>No orders yet</h2>
          <p style={{ color: '#666', margin: 0, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
            {businessIdFilter ? 'No orders match this business filter.' : 'Your orders from Mango businesses will appear here. Shop from the Marketplace or Search to get started.'}
          </p>
          <Link
            to="/marketplace"
            style={{
              display: 'inline-block',
              marginTop: 24,
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 10,
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            Browse Marketplace
          </Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filteredOrders.map((order) => (
            <div
              key={order.id}
              onClick={() => navigate(`/orders/${order.id}`)}
              style={{
                background: 'white',
                padding: 20,
                borderRadius: 12,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 16, color: '#333' }}>{order.businessName}</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{order.orderNumber}</div>
                <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                  {order.items?.length ?? 0} item{(order.items?.length ?? 0) !== 1 ? 's' : ''} • ${(order.total ?? 0).toFixed(2)}
                </div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{formatDate(order.createdAt)}</div>
              </div>
              <span style={{
                padding: '6px 12px',
                background: getStatusColor(order.status) + '20',
                color: getStatusColor(order.status),
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'capitalize',
              }}>
                {order.status ?? order.fulfillmentStatus ?? 'pending'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders;
